import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons/faCircleXmark";
import { alpha, Chip, type ChipProps, type Theme } from "@mui/material";
import { forwardRef } from "react";
import { FaIcon } from "../Icon/FaIcon";

export const srChipColors = [
  "default",
  "blue",
  "red",
  "amber",
  "green",
  "teal",
  "purple",
  "violet",
  "pink",
] as const;

export type SrChipColor = (typeof srChipColors)[number];
export type SrChipVariant = "filled" | "soft" | "outlined";
export type SrChipSize = "medium" | "small" | "xsmall";
export type SrChipShape = "rounded" | "pill";

export type SrChipProps = Omit<ChipProps, "clickable" | "color" | "size" | "variant"> & {
  /** Makes the chip keyboard-focusable and enables its interactive hover treatment. */
  clickable?: ChipProps["clickable"];
  color?: SrChipColor;
  shape?: SrChipShape;
  size?: SrChipSize;
  variant?: SrChipVariant;
};

const colorConfig = {
  amber: {
    paletteName: "sr25Amber",
    filled: { light: 300, dark: 500 },
    filledHover: { light: 400, dark: 400 },
    foreground: { light: 600, dark: 300 },
    outlined: { light: 300, dark: 300 },
    soft: { light: 300, dark: 300 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  blue: {
    paletteName: "sr25Blue",
    filled: { light: 400, dark: 600 },
    filledHover: { light: 500, dark: 500 },
    foreground: { light: 700, dark: 300 },
    outlined: { light: 400, dark: 500 },
    soft: { light: 500, dark: 500 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  default: {
    paletteName: "sr25Gray",
    filled: { light: 500, dark: 600 },
    filledHover: { light: 600, dark: 500 },
    foreground: { light: 950, dark: 300 },
    outlined: { light: 500, dark: 500 },
    soft: { light: 990, dark: 100 },
    softHoverOpacity: { light: 0.2, dark: 0.2 },
    softOpacity: { light: 0.1, dark: 0.1 },
  },
  green: {
    paletteName: "sr25Green",
    filled: { light: 500, dark: 600 },
    filledHover: { light: 600, dark: 500 },
    foreground: { light: 600, dark: 300 },
    outlined: { light: 500, dark: 500 },
    soft: { light: 400, dark: 400 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  pink: {
    paletteName: "sr25Pink",
    filled: { light: 400, dark: 600 },
    filledHover: { light: 500, dark: 500 },
    foreground: { light: 700, dark: 300 },
    outlined: { light: 400, dark: 400 },
    soft: { light: 500, dark: 500 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  purple: {
    paletteName: "sr25Purple",
    filled: { light: 500, dark: 600 },
    filledHover: { light: 600, dark: 500 },
    foreground: { light: 700, dark: 300 },
    outlined: { light: 500, dark: 300 },
    soft: { light: 500, dark: 500 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  red: {
    paletteName: "sr25Red",
    filled: { light: 400, dark: 600 },
    filledHover: { light: 500, dark: 500 },
    foreground: { light: 700, dark: 300 },
    outlined: { light: 400, dark: 400 },
    soft: { light: 500, dark: 500 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  teal: {
    paletteName: "sr25Teal",
    filled: { light: 400, dark: 500 },
    filledHover: { light: 500, dark: 400 },
    foreground: { light: 600, dark: 300 },
    outlined: { light: 400, dark: 400 },
    soft: { light: 300, dark: 300 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
  violet: {
    paletteName: "sr25Violet",
    filled: { light: 400, dark: 600 },
    filledHover: { light: 500, dark: 500 },
    foreground: { light: 700, dark: 300 },
    outlined: { light: 400, dark: 400 },
    soft: { light: 500, dark: 500 },
    softHoverOpacity: { light: 0.4, dark: 0.4 },
    softOpacity: { light: 0.2, dark: 0.2 },
  },
} as const;

type ColorMode = "light" | "dark";

type ChipColors = {
  activeBackground: string;
  activeBorder: string;
  background: string;
  border: string;
  deleteColor: string;
  foreground: string;
  hoverBackground: string;
  hoverBorder: string;
};

const OUTLINED_HOVER_BORDER_ALPHA = 0.7;
const OUTLINED_ACTIVE_BORDER_ALPHA = 0.9;

export const getSrChipColors = (
  theme: Theme,
  color: SrChipColor,
  variant: SrChipVariant,
  mode: ColorMode,
): ChipColors => {
  const config = colorConfig[color];
  const palette = theme.vars.palette[config.paletteName];
  const rawPalette = theme.palette[config.paletteName];
  const foreground = palette[config.foreground[mode]];
  const deleteColor =
    variant === "filled"
      ? theme.vars.palette.sr25Gray[0]
      : theme.vars.palette.sr25Gray[mode === "dark" ? 300 : 600];

  if (variant === "outlined") {
    return {
      // The active treatment carries the outlined/soft hover intensity one step
      // further, mirroring how the hover treatment already reuses the soft variant's
      // base intensity.
      activeBackground: alpha(rawPalette[config.soft[mode]], config.softHoverOpacity[mode]),
      activeBorder: alpha(rawPalette[config.outlined[mode]], OUTLINED_ACTIVE_BORDER_ALPHA),
      background: "transparent",
      border: alpha(rawPalette[config.outlined[mode]], 0.5),
      deleteColor,
      foreground,
      hoverBackground: alpha(rawPalette[config.soft[mode]], config.softOpacity[mode]),
      hoverBorder: alpha(rawPalette[config.outlined[mode]], OUTLINED_HOVER_BORDER_ALPHA),
    };
  }

  if (variant === "soft") {
    // Carry the base -> hover opacity step one step further for the active state.
    const hoverOpacity = config.softHoverOpacity[mode];
    // Rounded to avoid floating-point drift (e.g. 0.4 + (0.4 - 0.2) === 0.6000000000000001).
    const activeOpacity =
      Math.round((hoverOpacity + (hoverOpacity - config.softOpacity[mode])) * 100) / 100;

    return {
      activeBackground: alpha(rawPalette[config.soft[mode]], activeOpacity),
      activeBorder: "transparent",
      background: alpha(rawPalette[config.soft[mode]], config.softOpacity[mode]),
      border: "transparent",
      deleteColor,
      foreground,
      hoverBackground: alpha(rawPalette[config.soft[mode]], hoverOpacity),
      hoverBorder: "transparent",
    };
  }

  // Carry the base -> hover shade step one step further for the active state.
  const hoverShade = config.filledHover[mode];
  const activeShade = hoverShade + (hoverShade - config.filled[mode]);

  return {
    activeBackground: palette[activeShade],
    activeBorder: "transparent",
    background: palette[config.filled[mode]],
    border: "transparent",
    deleteColor,
    foreground: theme.vars.palette.sr25Gray[0],
    hoverBackground: palette[hoverShade],
    hoverBorder: "transparent",
  };
};

export const getSrChipSizeStyles = (theme: Theme, size: SrChipSize) => {
  if (size === "medium") {
    return {
      deleteIconSize: 14,
      height: 28,
      labelPadding: 8,
      roundedBorderRadius: theme.shape.borderRadiusMd,
      typography: theme.typography.body1,
    };
  }

  if (size === "xsmall") {
    return {
      deleteIconSize: 10,
      height: 16,
      labelPadding: 4,
      roundedBorderRadius: theme.shape.borderRadiusXs,
      typography: theme.typography.caption,
    };
  }

  return {
    deleteIconSize: 10,
    height: 20,
    labelPadding: 6,
    roundedBorderRadius: theme.shape.borderRadiusSm,
    typography: theme.typography.body2,
  };
};

const getColorStyles = (colors: ChipColors) => ({
  "--SrChip-active-background": colors.activeBackground,
  "--SrChip-active-border": colors.activeBorder,
  "--SrChip-delete-color": colors.deleteColor,
  "--SrChip-hover-background": colors.hoverBackground,
  "--SrChip-hover-border": colors.hoverBorder,
  backgroundColor: colors.background,
  borderColor: colors.border,
  color: colors.foreground,
});

export const SrChip = forwardRef<HTMLDivElement, SrChipProps>(function SrChip(
  {
    color = "default",
    deleteIcon,
    onDelete,
    shape = "rounded",
    size = "small",
    sx,
    variant = "filled",
    ...props
  },
  ref,
) {
  const usesDefaultDeleteIcon = Boolean(onDelete && !deleteIcon);

  return (
    <Chip
      {...props}
      ref={ref}
      data-color={color}
      data-shape={shape}
      data-size={size}
      data-variant={variant}
      deleteIcon={
        usesDefaultDeleteIcon ? (
          <span aria-hidden="true">
            <FaIcon data-icon="circle-xmark" icon={faCircleXmark} />
          </span>
        ) : (
          deleteIcon
        )
      }
      onDelete={onDelete}
      size={size}
      sx={[
        (theme) => {
          const lightColors = getSrChipColors(theme, color, variant, "light");
          const darkColors = getSrChipColors(theme, color, variant, "dark");
          const sizeStyles = getSrChipSizeStyles(theme, size);

          return {
            maxWidth: 1,
            border: "1px solid",
            borderRadius: `${
              shape === "pill" ? theme.shape.borderRadiusMax : sizeStyles.roundedBorderRadius
            }px`,
            height: sizeStyles.height,
            padding: 0,
            [theme.breakpoints.up("md")]: {
              padding: 0,
            },
            ...sizeStyles.typography,
            ...getColorStyles(lightColors),
            ...theme.applyStyles("dark", getColorStyles(darkColors)),
            transitionProperty: "background-color, border-color",
            transitionDuration: `${theme.transitions.duration.interactiveStateOut}ms`,
            "&.MuiChip-clickable:hover, &:has(.MuiChip-deleteIcon:hover), &:focus-visible": {
              transitionDuration: `${theme.transitions.duration.interactiveStateIn}ms`,
              backgroundColor: "var(--SrChip-hover-background)",
              borderColor: "var(--SrChip-hover-border)",
            },
            "&.MuiChip-clickable:active, &:has(.MuiChip-deleteIcon:active)": {
              transitionDuration: `${theme.transitions.duration.interactiveStateIn}ms`,
              backgroundColor: "var(--SrChip-active-background)",
              borderColor: "var(--SrChip-active-border)",
            },
            "& .MuiChip-avatar": {
              color: theme.vars.palette.sr25Gray[0],
            },
            "& .MuiChip-label, & .MuiChip-label.MuiChip-labelMedium": {
              paddingLeft: `${sizeStyles.labelPadding}px`,
              paddingRight: `${usesDefaultDeleteIcon ? 0 : sizeStyles.labelPadding}px`,
            },
            "& .MuiChip-deleteIcon, & .MuiChip-deleteIcon.MuiChip-deleteIconMedium": {
              alignItems: "center",
              backgroundColor: "transparent",
              color: "var(--SrChip-delete-color)",
              display: "inline-flex",
              flexShrink: 0,
              fontSize: sizeStyles.deleteIconSize,
              height: sizeStyles.deleteIconSize,
              justifyContent: "center",
              margin: `0 ${sizeStyles.labelPadding}px 0 4px`,
              opacity: 0.8,
              width: sizeStyles.deleteIconSize,
              "&:hover": {
                color: "var(--SrChip-delete-color)",
              },
              "& > svg": {
                fontSize: "inherit",
                height: "1em",
                width: "1em",
              },
            },
            '&[data-variant="soft"] .MuiChip-deleteIcon:hover, &[data-variant="outlined"] .MuiChip-deleteIcon:hover':
              {
                opacity: 1,
              },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
});
